import { DeleteOutline, FileOpenOutlined } from '@mui/icons-material';
import { Box, Button, Dialog, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useFieldArray, useForm, useFormContext } from 'react-hook-form';
import { uuidv7 } from 'uuidv7';
import type { z } from 'zod';
import { ReadOnlyField } from '../../../components/fields/ReadOnlyField.tsx';
import { Attachment } from '../../../types/index.ts';

export const AttachmentListField = () => {
  const { control } = useFormContext<{ attachments: Array<Attachment> }, 'attachments'>();

  const { fields, append, remove } = useFieldArray({
    name: 'attachments',
    control,
  });

  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <>
      <ReadOnlyField label="関連資料">
        <Stack spacing={1}>
          <AttachmentList
            items={fields}
            onDelete={(index) => {
              remove(index);
            }}
          />
          <Button
            variant="outlined"
            onClick={() => {
              setDialogOpen(true);
            }}
          >
            <FileOpenOutlined sx={{ marginRight: 1 }} />
            関連資料を追加
          </Button>
        </Stack>
      </ReadOnlyField>
      <Dialog open={dialogOpen}>
        <AttachmentForm
          onSubmit={(item) => {
            append(item);
            setDialogOpen(false);
          }}
          onCancel={() => {
            setDialogOpen(false);
          }}
        />
      </Dialog>
    </>
  );
};

export const AttachmentList = ({
  items,
  onDelete,
}: {
  items: Array<Attachment>;
  onDelete?: (index: number) => void;
}) => {
  return items.map((field, index) => (
    <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
      <FileOpenOutlined sx={{ margin: 1 }} />
      <Box flex={1}>
        <Typography variant="body1">{field.title}</Typography>
        <Typography
          component="a"
          variant="caption"
          color="textPrimary"
          href={field.url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {field.url}
        </Typography>
      </Box>
      {onDelete && (
        <Button
          color="error"
          onClick={() => onDelete(index)}
          size="small"
          sx={{ minWidth: 'revert' }}
        >
          <DeleteOutline />
        </Button>
      )}
    </Box>
  ));
};

const AttachmentForm = ({
  onSubmit,
  onCancel,
}: {
  onSubmit: (item: Attachment) => void;
  onCancel: () => void;
}) => {
  const { register, handleSubmit, formState } = useForm({
    defaultValues: {
      title: '',
      url: '',
    },
  });

  return (
    <Stack spacing={2} sx={{ padding: 2, width: '600px' }}>
      <Typography variant="h5">関連資料を追加</Typography>
      <TextField
        {...register('title', { required: 'ファイル名は必須です' })}
        variant="outlined"
        label="ファイル名"
        error={!!formState.errors.title}
        helperText={formState.errors.title?.message}
      />
      <TextField
        {...register('url', {
          required: 'URLは必須です',
          validate(v) {
            if (!v) return 'URLは必須です';
            try {
              new URL(v);
              return true;
            } catch {
              return '有効なURLを入力してください';
            }
          },
        })}
        variant="outlined"
        label="URL"
        placeholder="https://"
        error={!!formState.errors.url}
        helperText={formState.errors.url?.message}
      />
      <Button
        variant="contained"
        onClick={handleSubmit((data) => {
          const item = Attachment.parse({
            ...data,
            id: uuidv7(),
            publishedAt: new Date(),
            publishedBy: 'system',
          } satisfies z.input<typeof Attachment>);

          onSubmit(item);
        })}
      >
        Add
      </Button>
      <Button variant="outlined" onClick={onCancel}>
        Cancel
      </Button>
    </Stack>
  );
};
